import React, { useEffect, useRef, useState } from "react";
import { Button } from "@chakra-ui/react";
import { Download } from "lucide-react";
import { toaster } from "../../../components/toaster";
import ActivityToast from "../../../components/ActivityToast";
import mogomaLogo from "../../../assets/logo.png";

const DriverPartnerPoster = ({
    user,
    driverPartner,
    mediaBaseUrl,
    canDownloadPoster,
}) => {
    const canvasRef = useRef(null);

    const [profileImage, setProfileImage] =
        useState(null);
    const [isDownloading, setIsDownloading] = useState(false);


    useEffect(() => {
        const profileDocument =
            driverPartner?.documents?.find(
                (doc) =>
                    doc.document_type === "Profile Image"
            );

        if (profileDocument?.file_path) {
            const baseUrl =
                mediaBaseUrl || "";

            const path =
                profileDocument.file_path;

            setProfileImage(
                `${baseUrl}${
                    path.startsWith("/")
                        ? ""
                        : "/"
                }${path}`
            );
        } else {
            setProfileImage(null);
        }
    }, [driverPartner, mediaBaseUrl]);

    // =========================================================
    // DATA
    // =========================================================

    const fullName = [
        user?.first_name,
        user?.last_name,
    ]
    
        .filter(Boolean)
        .join(" ")
        .trim() || "Mogoma Driver Partner";

    const phone =
        user?.phone_number ||
        "Not provided";

    const email =
        user?.email ||
        "Not provided";

    const referralCode =
        driverPartner?.partner_referral_code ||
        "N/A";

    const partnerId =
        driverPartner?.driver_partner_id ||
        "N/A";

    const status =
        driverPartner?.status ||
        "Active";

    const joinedDate =
        driverPartner?.created_at
            ? new Date(
                  driverPartner.created_at
              ).toLocaleDateString(
                  "en-GB",
                  {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                  }
              )
            : "N/A";

    // =========================================================
    // COLORS
    // =========================================================

    const COLORS = {
        navy: "#071D54",
        blue: "#2453FF",
        darkBlue: "#123DDA",
        white: "#FFFFFF",
        background: "#F5F7FC",
        paleBlue: "#EAF1FF",
        lightBlue: "#EDF3FF",
        border: "#E0E6F0",
        muted: "#59657D",
    };

    // =========================================================
    // CANVAS HELPERS
    // =========================================================

    const roundedRect = (
        ctx,
        x,
        y,
        width,
        height,
        radius,
        fill,
        stroke = null,
        strokeWidth = 1
    ) => {
        ctx.beginPath();

        ctx.roundRect(
            x,
            y,
            width,
            height,
            radius
        );

        if (fill) {
            ctx.fillStyle = fill;
            ctx.fill();
        }

        if (stroke) {
            ctx.strokeStyle = stroke;
            ctx.lineWidth = strokeWidth;
            ctx.stroke();
        }
    };

    const drawText = (
        ctx,
        value,
        x,
        y,
        size,
        weight,
        color,
        align = "left",
        family = "Arial"
    ) => {
        ctx.font =
            `${weight} ${size}px ${family}`;

        ctx.fillStyle = color;
        ctx.textAlign = align;
        ctx.textBaseline = "alphabetic";

        ctx.fillText(
            value,
            x,
            y
        );
    };

const drawWrappedText = (
    ctx,
    text,
    x,
    y,
    maxWidth,
    size,
    weight,
    color,
    lineHeight,
    family = "Arial"
) => {
    ctx.font = `${weight} ${size}px ${family}`;
    ctx.fillStyle = color;
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";

    const words = text.split(" ");
    const lines = [];
    let currentLine = "";

    words.forEach((word) => {
        const testLine = currentLine
            ? `${currentLine} ${word}`
            : word;

        if (
            ctx.measureText(testLine).width <=
            maxWidth
        ) {
            currentLine = testLine;
        } else {
            if (currentLine) {
                lines.push(currentLine);
            }

            currentLine = word;
        }
    });

    if (currentLine) {
        lines.push(currentLine);
    }

    lines.forEach((line, index) => {
        ctx.fillText(
            line,
            x,
            y + index * lineHeight
        );
    });

    return lines.length;
};
    const drawLine = (
        ctx,
        x1,
        y1,
        x2,
        y2,
        color,
        width = 1
    ) => {
        ctx.beginPath();

        ctx.moveTo(
            x1,
            y1
        );

        ctx.lineTo(
            x2,
            y2
        );

        ctx.strokeStyle = color;
        ctx.lineWidth = width;
        ctx.stroke();
    };

    const drawCircle = (
        ctx,
        x,
        y,
        radius,
        fill
    ) => {
        ctx.beginPath();

        ctx.arc(
            x,
            y,
            radius,
            0,
            Math.PI * 2
        );

        ctx.fillStyle = fill;
        ctx.fill();
    };


    // =========================================================
    // LOAD IMAGE
    // =========================================================

    const loadImage = (src) => {
        return new Promise(
            (resolve) => {
                if (!src) {
                    resolve(null);
                    return;
                }

                const image =
                    new Image();

                image.crossOrigin =
                    "anonymous";

                image.onload = () =>
                    resolve(image);

                image.onerror = () =>
                    resolve(null);

                image.src = src;
            }
        );
    };

    // =========================================================
    // DRAW ICON BOX
    // =========================================================

    const drawIconBox = (
        ctx,
        x,
        y,
        type
    ) => {
        roundedRect(
            ctx,
            x,
            y,
            66,
            66,
            15,
            COLORS.paleBlue
        );

        const centerX =
            x + 33;

        const centerY =
            y + 33;

        ctx.strokeStyle =
            COLORS.blue;

        ctx.fillStyle =
            COLORS.blue;

        ctx.lineWidth = 3;

        ctx.lineCap = "round";

        // PHONE
// PHONE
if (type === "phone") {
    ctx.save();

    ctx.strokeStyle = COLORS.blue;
    ctx.lineWidth = 4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    ctx.beginPath();

    // Top earpiece
    ctx.moveTo(
        centerX - 11,
        centerY - 15
    );

    ctx.quadraticCurveTo(
        centerX - 15,
        centerY - 17,
        centerX - 17,
        centerY - 12
    );

    // Left side of handset
    ctx.quadraticCurveTo(
        centerX - 16,
        centerY + 4,
        centerX - 5,
        centerY + 13
    );

    // Bottom/right curve
    ctx.quadraticCurveTo(
        centerX + 5,
        centerY + 20,
        centerX + 14,
        centerY + 13
    );

    // Bottom earpiece
    ctx.quadraticCurveTo(
        centerX + 18,
        centerY + 10,
        centerX + 14,
        centerY + 6
    );

    // Inner right section
    ctx.lineTo(
        centerX + 7,
        centerY + 1
    );

    // Return through handset
    ctx.quadraticCurveTo(
        centerX + 4,
        centerY,
        centerX + 2,
        centerY + 3
    );

    ctx.lineTo(
        centerX - 1,
        centerY + 6
    );

    // Inner grip
    ctx.quadraticCurveTo(
        centerX - 7,
        centerY + 2,
        centerX - 10,
        centerY - 4
    );

    ctx.lineTo(
        centerX - 7,
        centerY - 9
    );

    ctx.quadraticCurveTo(
        centerX - 8,
        centerY - 13,
        centerX - 11,
        centerY - 15
    );

    ctx.stroke();

    ctx.restore();
}

        // EMAIL
        if (type === "email") {
            ctx.lineWidth = 3;

            ctx.strokeRect(
                centerX - 18,
                centerY - 13,
                36,
                27
            );

            ctx.beginPath();

            ctx.moveTo(
                centerX - 18,
                centerY - 12
            );

            ctx.lineTo(
                centerX,
                centerY + 2
            );

            ctx.lineTo(
                centerX + 18,
                centerY - 12
            );

            ctx.stroke();
        }

        // DRIVER ID
        if (type === "id") {
            roundedRect(
                ctx,
                x + 15,
                y + 17,
                36,
                31,
                4,
                COLORS.blue
            );

            drawCircle(
                ctx,
                centerX - 8,
                centerY - 5,
                5,
                COLORS.white
            );

            drawLine(
                ctx,
                centerX,
                centerY - 7,
                centerX + 12,
                centerY - 7,
                COLORS.white,
                2
            );

            drawLine(
                ctx,
                centerX,
                centerY + 1,
                centerX + 12,
                centerY + 1,
                COLORS.white,
                2
            );

            drawLine(
                ctx,
                centerX,
                centerY + 9,
                centerX + 8,
                centerY + 9,
                COLORS.white,
                2
            );
        }

        // CALENDAR
        if (type === "calendar") {
            ctx.lineWidth = 3;

            ctx.strokeRect(
                centerX - 17,
                centerY - 14,
                34,
                31
            );

            drawLine(
                ctx,
                centerX - 17,
                centerY - 5,
                centerX + 17,
                centerY - 5,
                COLORS.blue,
                3
            );

            drawLine(
                ctx,
                centerX - 9,
                centerY - 20,
                centerX - 9,
                centerY - 9,
                COLORS.blue,
                4
            );

            drawLine(
                ctx,
                centerX + 9,
                centerY - 20,
                centerX + 9,
                centerY - 9,
                COLORS.blue,
                4
            );

            [
                [-9, 4],
                [0, 4],
                [9, 4],
                [-9, 13],
                [0, 13],
                [9, 13],
            ].forEach(
                ([dx, dy]) => {
                    drawCircle(
                        ctx,
                        centerX + dx,
                        centerY + dy,
                        2,
                        COLORS.blue
                    );
                }
            );
        }

        // SHIELD
        if (type === "shield") {
            ctx.beginPath();

            ctx.moveTo(
                centerX,
                centerY - 18
            );

            ctx.lineTo(
                centerX + 17,
                centerY - 11
            );

            ctx.lineTo(
                centerX + 14,
                centerY + 9
            );

            ctx.quadraticCurveTo(
                centerX,
                centerY + 20,
                centerX,
                centerY + 20
            );

            ctx.quadraticCurveTo(
                centerX,
                centerY + 20,
                centerX - 14,
                centerY + 9
            );

            ctx.lineTo(
                centerX - 17,
                centerY - 11
            );

            ctx.closePath();

            ctx.stroke();

            ctx.beginPath();

            ctx.moveTo(
                centerX - 7,
                centerY
            );

            ctx.lineTo(
                centerX - 1,
                centerY + 6
            );

            ctx.lineTo(
                centerX + 9,
                centerY - 6
            );

            ctx.stroke();
        }
    };

    // =========================================================
    // VERIFIED ICON
    // =========================================================

 const drawVerifiedIcon = (
    ctx,
    x,
    y
) => {
    const outerRadius = 18;
    const innerRadius = 15;
    const points = 12;

    ctx.save();

    // Verified badge / scalloped shape
    ctx.beginPath();

    for (let i = 0; i < points * 2; i++) {
        const angle =
            (Math.PI * 2 * i) /
            (points * 2) -
            Math.PI / 2;

        const radius =
            i % 2 === 0
                ? outerRadius
                : innerRadius;

        const px =
            x +
            Math.cos(angle) *
                radius;

        const py =
            y +
            Math.sin(angle) *
                radius;

        if (i === 0) {
            ctx.moveTo(px, py);
        } else {
            ctx.lineTo(px, py);
        }
    }

    ctx.closePath();

    ctx.fillStyle =
        COLORS.blue;

    ctx.fill();

    // White verification check
    ctx.strokeStyle =
        COLORS.white;

    ctx.lineWidth = 3.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    ctx.beginPath();

    ctx.moveTo(
        x - 7,
        y
    );

    ctx.lineTo(
        x - 1,
        y + 6
    );

    ctx.lineTo(
        x + 9,
        y - 7
    );

    ctx.stroke();

    ctx.restore();
};

    // =========================================================
    // DRAW POSTER
    // =========================================================

    const drawPoster = async () => {
        const canvas =
            canvasRef.current;

        if (!canvas) return;

        const ctx =
            canvas.getContext("2d");

        const WIDTH = 1086;
        const HEIGHT = 1208;

        canvas.width =
            WIDTH;

        canvas.height =
            HEIGHT;

        // =====================================================
        // LOAD IMAGES
        // =====================================================

        const profileImg =
            await loadImage(
                profileImage
            );

   const logoImg =
    await loadImage(
        mogomaLogo
    );

        // =====================================================
        // BACKGROUND
        // =====================================================

        ctx.clearRect(
            0,
            0,
            WIDTH,
            HEIGHT
        );

        const background =
            ctx.createLinearGradient(
                0,
                0,
                WIDTH,
                HEIGHT
            );

        background.addColorStop(
            0,
            "#FFFFFF"
        );

        background.addColorStop(
            0.55,
            "#FAFBFF"
        );

        background.addColorStop(
            1,
            "#EDF3FF"
        );

        ctx.fillStyle =
            background;

        ctx.fillRect(
            0,
            0,
            WIDTH,
            HEIGHT
        );

        // =====================================================
        // TOP RIGHT BLUE SHAPE
        // =====================================================

        ctx.fillStyle =
            COLORS.darkBlue;

        ctx.beginPath();

        ctx.moveTo(
            800,
            0
        );

        ctx.lineTo(
            WIDTH,
            0
        );

        ctx.lineTo(
            WIDTH,
            280
        );

        ctx.bezierCurveTo(
            1010,
            250,
            970,
            215,
            925,
            185
        );

        ctx.bezierCurveTo(
            870,
            150,
            845,
            115,
            825,
            70
        );

        ctx.bezierCurveTo(
            815,
            35,
            805,
            12,
            800,
            0
        );

        ctx.closePath();

        ctx.fill();

        // =====================================================
        // PALE BACKGROUND LOGO
        // =====================================================

        if (logoImg) {
    ctx.save();

    ctx.globalAlpha = 0.18;

    ctx.drawImage(
        logoImg,
        700,
        75,
        300,
        210
    );

    ctx.restore();
}

        // =====================================================
        // TOP RIGHT LOGO
        // =====================================================

       if (logoImg) {
    ctx.drawImage(
        logoImg,
        955,
        55,
        95,
        66
    );
} 

        // =====================================================
        // MAIN LOGO
        // =====================================================

       if (logoImg) {
    ctx.drawImage(
        logoImg,
        55,
        92,
        115,
        80
    );
}

drawText(
    ctx,
    "Mogoma",
    185,
    135,
    55,
    "700",
    COLORS.navy,
    "left",
    "Georgia"
);

drawText(
    ctx,
    "Motshwarele gone hoo...!",
    188,
    165,
    18,
    "italic 500",
    COLORS.blue,
    "left",
    "Arial"
);

        // =====================================================
        // MAIN TITLE
        // =====================================================

        drawText(
    ctx,
    "I AM A",
    70,
    385,
    65,
    "900",
    COLORS.navy,
    "left",
    "Arial"
);
        drawText(
            ctx,
            "MOGOMA DRIVER PARTNER",
            70,
            449,
            65,
            "900",
            COLORS.blue,
            "left",
            "Arial"
        );

        drawText(
            ctx,
            "Partner today, Grow Together.",
            72,
            508,
            29,
            "500",
            COLORS.navy
        );

        roundedRect(
            ctx,
            72,
            536,
            80,
            9,
            5,
            COLORS.blue
        );

        drawCircle(
            ctx,
            168,
            540,
            5,
            COLORS.blue
        );

        // =====================================================
        // MAIN WHITE CARD
        // =====================================================

        ctx.save();

        ctx.shadowColor =
            "rgba(7,29,84,0.10)";

        ctx.shadowBlur = 30;

        ctx.shadowOffsetY = 8;

        roundedRect(
            ctx,
            53,
            577,
            980,
            590,
            30,
            COLORS.white
        );

        ctx.restore();

        // =====================================================
        // PROFILE IMAGE CIRCLE
        // =====================================================

        drawCircle(
            ctx,
            197,
            711,
            100,
            COLORS.paleBlue
        );

        ctx.save();

        ctx.beginPath();

        ctx.arc(
            197,
            711,
            91,
            0,
            Math.PI * 2
        );

        ctx.clip();

        if (profileImg) {
            ctx.drawImage(
                profileImg,
                106,
                620,
                182,
                182
            );
 } else if (logoImg) {
    ctx.drawImage(
        logoImg,
        145,
        670,
        105,
        73
    );
}
        ctx.restore();

        // =====================================================
        // NAME
        // =====================================================

const nameLines = drawWrappedText(
    ctx,
    fullName.toUpperCase(),
    331,
    673,
    650,
    40,
    "900",
    COLORS.navy,
    46,
    "Arial"
);

// =====================================================
// VERIFIED
// =====================================================

const verifiedY =
    nameLines > 1
        ? 782
        : 735;

drawVerifiedIcon(
    ctx,
    352,
    verifiedY
);

drawText(
    ctx,
    "Verified Mogoma Driver Partner",
    386,
    verifiedY + 8,
    20,
    "700",
    COLORS.blue
);

        // =====================================================
        // DIVIDER
        // =====================================================

        drawLine(
            ctx,
            88,
            840,
            998,
            840,
            COLORS.border,
            2
        );

        // =====================================================
        // PHONE
        // =====================================================

        drawIconBox(
            ctx,
            92,
            870,
            "phone"
        );

        drawText(
            ctx,
            "PHONE NUMBER",
            185,
            894,
            14,
            "700",
            COLORS.muted
        );

        drawText(
            ctx,
            phone,
            185,
            927,
            22,
            "800",
            COLORS.navy
        );

        // =====================================================
        // VERTICAL DIVIDER
        // =====================================================

        drawLine(
            ctx,
            502,
            869,
            502,
            934,
            COLORS.border,
            2
        );

        // =====================================================
        // EMAIL
        // =====================================================

        drawIconBox(
            ctx,
            545,
            870,
            "email"
        );

        drawText(
            ctx,
            "EMAIL ADDRESS",
            638,
            894,
            14,
            "700",
            COLORS.muted
        );

        drawText(
            ctx,
            email,
            638,
            927,
            19,
            "800",
            COLORS.navy
        );

        // =====================================================
        // SECOND DIVIDER
        // =====================================================

        drawLine(
            ctx,
            88,
            967,
            998,
            967,
            COLORS.border,
            2
        );

        // =====================================================
        // RECRUITMENT CODE CARD
        // =====================================================

        roundedRect(
            ctx,
            88,
            990,
            430,
            145,
            15,
            COLORS.lightBlue
        );

        drawText(
            ctx,
            "DRIVER PARTNER ID",
            109,
            1026,
            15,
            "900",
            COLORS.blue
        );

        drawText(
            ctx,
            referralCode,
            109,
            1073,
            36,
            "900",
            COLORS.blue,
            "left",
            "Arial"
        );

   drawText(
    ctx,
    "Keep your Driver Partner ID safe.",
    109,
    1108,
    16,
    "500",
    COLORS.navy
);

drawText(
    ctx,
    "You may need it when contacting support.",
    109,
    1132,
    16,
    "500",
    COLORS.navy
);

        roundedRect(
            ctx,
            109,
            1150,
            45,
            5,
            3,
            COLORS.blue
        );

        drawCircle(
            ctx,
            160,
            1152,
            4,
            COLORS.blue
        );

    
    

        // =====================================================
        // JOINED
        // =====================================================

        drawIconBox(
            ctx,
            674,
            1008,
            "calendar"
        );

        drawText(
            ctx,
            "JOINED ON",
            707,
            1098,
            11,
            "700",
            COLORS.muted,
            "center"
        );

        drawText(
            ctx,
            joinedDate,
            707,
            1130,
            15,
            "900",
            COLORS.navy,
            "center"
        );

        // =====================================================
        // STATUS
        // =====================================================

        drawIconBox(
            ctx,
            848,
            1008,
            "shield"
        );

        drawText(
            ctx,
            "STATUS",
            881,
            1098,
            11,
            "700",
            COLORS.muted,
            "center"
        );

        drawText(
            ctx,
            status
                .toUpperCase(),
            881,
            1130,
            14,
            "900",
            COLORS.blue,
            "center"
        );
    };

    // =========================================================
    // DOWNLOAD
    // =========================================================

    const handleDownload = async () => {
        try {
            await drawPoster();

            const canvas =
                canvasRef.current;

            if (!canvas) {
                return;
            }

            const link =
                document.createElement("a");

            link.download =
                `mogoma-driver-partner-${referralCode}.png`;

            link.href =
                canvas.toDataURL(
                    "image/png",
                    1
                );

            link.click();

        } catch (error) {
            console.error(
                "Failed to generate Driver Partner poster:",
                error
            );
        }
    };

    // =========================================================
    // RENDER
    // =========================================================
const handlePosterDownload = async () => {
    if (!canDownloadPoster) {
        toaster.create({
            render: () => (
                <ActivityToast
                    type="warning"
                    title="Complete Your Profile"
                    description="Please complete your profile and ensure your Driver Partner account is approved before downloading your poster."
                    timestamp="Just now"
                />
            ),
        });

        return;
    }

    try {
        setIsDownloading(true);

        await new Promise((resolve) =>
            setTimeout(resolve, 4000)
        );

        await handleDownload();

        toaster.create({
            render: () => (
                <ActivityToast
                    type="success"
                    title="Poster Downloaded"
                    description="Your Driver Partner poster has been downloaded successfully."
                    timestamp="Just now"
                />
            ),
        });
    } finally {
        setIsDownloading(false);
    }
};

    return (
        <>
            {/* Hidden canvas used for poster generation */}
            <canvas
                ref={canvasRef}
                width={1086}
                height={1208}
                style={{
                    display: "none",
                }}
            />

          <Button
    size="sm"
    bg="#2453FF"
    color="white"
    borderRadius="10px"
    px={5}
    opacity={
        canDownloadPoster && !isDownloading
            ? 1
            : 0.6
    }
    cursor={
        canDownloadPoster && !isDownloading
            ? "pointer"
            : "not-allowed"
    }
    _hover={{
        bg:
            canDownloadPoster && !isDownloading
                ? "#1744E8"
                : "#2453FF",
    }}
    onClick={handlePosterDownload}
    loading={isDownloading}
    loadingText="Downloading..."
>
    {!isDownloading && (
        <Download
            size={16}
            style={{
                marginRight: 8,
            }}
        />
    )}

    {isDownloading
        ? "Downloading..."
        : "Download Poster"}
</Button>
        </>
    );
};

export default DriverPartnerPoster;